/**
 * Quote Interpolator
 *
 * Generates fake quotes between historical bars to simulate more realistic
 * market conditions during replay mode.
 *
 * Features:
 * - Interpolates OHLC values between bars
 * - Generates 4 quotes per bar by default (configurable)
 * - Simulates price movement within bar's OHLC range
 * - Maintains volume distribution
 *
 * Algorithm:
 * For each bar, generates quotes in this sequence:
 * 1. Open -> High (upward movement)
 * 2. High -> Low (downward movement)
 * 3. Low -> Close (final movement)
 *
 * This creates realistic intra-bar price action.
 */

class QuoteInterpolator {
  /**
   * Interpolate quotes between bars
   * @param {Object} prevBar - Previous bar (or null for first bar)
   * @param {Object} currentBar - Current bar to interpolate
   * @param {string} symbol - Symbol
   * @param {number} quotesPerBar - Number of quotes to generate per bar (default: 4)
   * @returns {Array} Array of interpolated quotes
   */
  interpolate(prevBar, currentBar, symbol, quotesPerBar = 4) {
    const quotes = [];
    const { open, high, low, close, volume, date } = currentBar;

    // Generate quotes following typical price movement pattern
    // Pattern: Open -> High -> Low -> Close
    const steps = Math.max(4, quotesPerBar);

    for (let i = 0; i < steps; i++) {
      const progress = i / (steps - 1); // 0 to 1
      const quote = this.generateQuote(
        { open, high, low, close, volume, date },
        progress,
        symbol,
        i,
        steps,
      );
      quotes.push(quote);
    }

    return quotes;
  }

  /**
   * Generate a single interpolated quote
   * @param {Object} bar - Bar data
   * @param {number} progress - Progress through bar (0 to 1)
   * @param {string} symbol - Symbol
   * @param {number} step - Current step
   * @param {number} totalSteps - Total steps
   * @returns {Object} Quote object
   */
  generateQuote(bar, progress, symbol, step, totalSteps) {
    const { open, high, low, close, volume, date } = bar;

    // Determine price movement path: Open -> High -> Low -> Close
    let price;
    let currentHigh = open;
    let currentLow = open;

    if (totalSteps === 4) {
      // Optimized for 4 quotes
      switch (step) {
        case 0:
          // Opening quote
          price = open;
          currentHigh = Math.max(open, high);
          currentLow = Math.min(open, low);
          break;
        case 1:
          // Move toward high
          price = this.lerp(open, high, 0.7);
          currentHigh = high;
          currentLow = Math.min(open, low);
          break;
        case 2:
          // Move toward low
          price = this.lerp(high, low, 0.6);
          currentHigh = high;
          currentLow = low;
          break;
        case 3:
          // Closing quote
          price = close;
          currentHigh = high;
          currentLow = low;
          break;
      }
    } else {
      // Generic algorithm for any number of steps
      if (progress < 0.33) {
        // Phase 1: Open -> High
        const phaseProgress = progress / 0.33;
        price = this.lerp(open, high, phaseProgress);
        currentHigh = Math.max(open, price);
        currentLow = open;
      } else if (progress < 0.66) {
        // Phase 2: High -> Low
        const phaseProgress = (progress - 0.33) / 0.33;
        price = this.lerp(high, low, phaseProgress);
        currentHigh = high;
        currentLow = Math.min(low, price);
      } else {
        // Phase 3: Low -> Close
        const phaseProgress = (progress - 0.66) / 0.34;
        price = this.lerp(low, close, phaseProgress);
        currentHigh = high;
        currentLow = low;
      }
    }

    // Distribute volume across quotes
    const quoteVolume = Math.floor(volume / totalSteps);

    // Create quote object
    return {
      S: symbol,
      DT: date.toISOString(),
      O: open,
      H: currentHigh,
      L: currentLow,
      C: price,
      Q: quoteVolume,
      _isReplay: true,
      _isInterpolated: true,
      _interpolationStep: step,
      _interpolationProgress: progress,
    };
  }

  /**
   * Linear interpolation between two values
   * @param {number} start - Start value
   * @param {number} end - End value
   * @param {number} t - Progress (0 to 1)
   * @returns {number} Interpolated value
   */
  lerp(start, end, t) {
    return start + (end - start) * t;
  }

  /**
   * Get recommended quotes per bar based on periodicity
   * @param {string} periodicity - Chart periodicity
   * @returns {number} Recommended quotes per bar
   */
  getRecommendedQuotesPerBar(periodicity) {
    const match = periodicity.match(/^(\d+)(min|h|D|W|M)$/);
    if (!match) return 4; // Default

    const [, value, unit] = match;
    const num = parseInt(value, 10);

    switch (unit) {
      case "min":
        // 1min = 4 quotes (every 15s)
        // 5min = 20 quotes (every 15s)
        return num * 4;
      case "h":
        // 1h = 60 quotes (every minute)
        return num * 60;
      case "D":
        // 1D = 240 quotes (every 6 minutes)
        return num * 240;
      case "W":
      case "M":
        // Weekly/Monthly: just 10 quotes per bar
        return 10;
      default:
        return 4;
    }
  }
}

// Singleton instance
const quoteInterpolator = new QuoteInterpolator();

export default quoteInterpolator;
